'use client';

import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useTranslations } from 'next-intl';
import { useParams } from 'next/navigation';
import Link from 'next/link';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';

import { Button } from '@/src/shared/ui/button';

import { verifyEmailSchema } from '../model/schemas';
import type { VerifyEmailFormValues, VerifyEmailState } from '../model/types';
import { verifyEmailAction } from '../api/verifyEmailAction';
import { isRedirectError } from '../lib/isRedirectError';
import { VerifyEmailOtpField } from './VerifyEmailOtpField';
import { mapVerifyEmailApiError } from './mapVerifyEmailApiError';
import { useVerifyEmailResend } from './useVerifyEmailResend';

type Props = {
  email: string;
};

export function VerifyEmailForm({ email }: Props) {
  const t = useTranslations('auth.verifyEmail');
  const params = useParams();
  const locale = (params.locale as string) ?? 'es';
  const [formState, setFormState] = useState<VerifyEmailState>('idle');

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<VerifyEmailFormValues>({
    resolver: yupResolver(verifyEmailSchema),
    defaultValues: { code: '' },
  });

  const submitting = formState === 'submitting';
  const { resending, cooldown, resendDisabled, onResend } = useVerifyEmailResend({
    email,
    mapApiError: (code) => mapVerifyEmailApiError(t, code),
    isBusy: submitting,
  });

  async function onSubmit(data: VerifyEmailFormValues) {
    setFormState('submitting');
    try {
      const result = await verifyEmailAction({ email, code: data.code }, locale);
      if (result && !result.ok) {
        toast.error(mapVerifyEmailApiError(t, result.code));
        setFormState('error');
      }
    } catch (err) {
      // A successful verification redirects, and Next signals that by throwing.
      if (isRedirectError(err)) throw err;
      toast.error(mapVerifyEmailApiError(t, 'server_error'));
      setFormState('error');
    }
  }

  return (
    <form
      method="post"
      onSubmit={handleSubmit(onSubmit)}
      noValidate
      className="flex w-full flex-col gap-4"
    >
      <VerifyEmailOtpField control={control} errors={errors} />

      <Button type="submit" disabled={submitting} className="h-11 w-full rounded-lg text-[14px]">
        {submitting ? (
          <>
            <Loader2 className="animate-spin" />
            {t('submitting')}
          </>
        ) : (
          t('submit')
        )}
      </Button>

      <Button
        type="button"
        variant="outline"
        disabled={resendDisabled}
        onClick={onResend}
        className="h-11 w-full rounded-lg text-[14px]"
      >
        {resending ? (
          <>
            <Loader2 className="animate-spin" />
            {t('resend.submitting')}
          </>
        ) : cooldown > 0 ? (
          t('resend.cooldown', { seconds: cooldown })
        ) : (
          t('resend.submit')
        )}
      </Button>

      <div className="text-center text-xs text-muted-foreground">
        <Link
          href={`/${locale}/login`}
          className="hover:text-foreground underline-offset-4 hover:underline"
        >
          {t('links.login')}
        </Link>
      </div>
    </form>
  );
}
